import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconDownload, IconPencil, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import {
  ITEM_DOCUMENT_MAX_BYTES,
  downloadItemDocument,
  listItemDocuments,
  removeItemDocument,
  renameItemDocument,
  uploadItemDocument,
  type ItemDocumentDto,
} from './api';

interface Props {
  itemId: string;
  /** False while the operator has no write permission — the list stays readable and downloadable. */
  canWrite: boolean;
}

/** Bytes as something a person reads. Whole numbers: nobody needs 1.37 MB to two places here. */
function readableSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * An item's attached files — spec sheets, safety data sheets, drawings.
 *
 * A table rather than the tile grid the photos use, because these are read across: name, then
 * what somebody said about it, then how big it is. There is nothing to look at.
 *
 * Only rendered while EDITING, for the same reason as the photos: a document needs an item to
 * belong to, and on a new item there is not one yet.
 */
export function ItemDocuments({ itemId, canWrite }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const resetRef = useRef<() => void>(null);

  const documents = useQuery({
    queryKey: ['item-documents', itemId] as const,
    queryFn: ({ signal }) => listItemDocuments(itemId, signal),
  });

  function settle(rows: ItemDocumentDto[]) {
    queryClient.setQueryData(['item-documents', itemId], rows);
    setEditing(null);
    setError(null);
  }

  function fail(e: unknown) {
    // Both detail and title are optional on a Problem Details document, so neither can be relied
    // on — our own wording beats rendering "undefined".
    const problem = e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : null;
    setError(problem ?? t('items.documents.failed'));
  }

  const upload = useMutation({
    mutationFn: (file: File) => uploadItemDocument(itemId, file),
    onSuccess: settle,
    onError: fail,
  });

  const rename = useMutation({
    mutationFn: (v: { id: string; name: string; description: string }) =>
      renameItemDocument(itemId, v.id, v.name, v.description.trim() || null),
    onSuccess: settle,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeItemDocument(itemId, id),
    onSuccess: settle,
    onError: fail,
  });

  const download = useMutation({
    mutationFn: (doc: ItemDocumentDto) => downloadItemDocument(itemId, doc),
    onError: fail,
  });

  const busy = upload.isPending || rename.isPending || remove.isPending;
  const rows = documents.data ?? [];

  function choose(file: File | null) {
    // FileButton holds the chosen file until reset, so picking the same one twice running would
    // otherwise do nothing the second time.
    resetRef.current?.();
    if (!file) return;
    if (file.size > ITEM_DOCUMENT_MAX_BYTES) {
      setError(t('items.documents.tooLarge', { max: ITEM_DOCUMENT_MAX_BYTES / 1024 / 1024 }));
      return;
    }
    upload.mutate(file);
  }

  function startEditing(doc: ItemDocumentDto) {
    setEditing(doc.itemDocumentId);
    setDraftName(doc.documentName);
    setDraftDescription(doc.description ?? '');
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={500}>
          {t('items.documents.section')}
        </Text>
        {canWrite && (
          <FileButton resetRef={resetRef} onChange={choose}>
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={upload.isPending}
                disabled={busy}
              >
                {t('items.documents.add')}
              </Button>
            )}
          </FileButton>
        )}
      </Group>

      <Text size="xs" c="dimmed">
        {t('items.documents.hint')}
      </Text>

      {error && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {documents.isLoading && (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      )}

      {!documents.isLoading && rows.length === 0 && (
        <Card withBorder padding="md">
          <Text size="sm" c="dimmed" ta="center">
            {t('items.documents.none')}
          </Text>
        </Card>
      )}

      {rows.length > 0 && (
        <Card withBorder padding={0}>
          <Table.ScrollContainer minWidth={420} mah={280}>
            <Table striped highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('items.documents.name')}</Table.Th>
                  <Table.Th w={90} ta="right">{t('items.documents.size')}</Table.Th>
                  <Table.Th w={110} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((d) => {
                  const isEditing = editing === d.itemDocumentId;
                  return (
                    <Table.Tr key={d.itemDocumentId}>
                      <Table.Td>
                        {isEditing ? (
                          <Stack gap={4}>
                            <TextInput
                              size="xs"
                              aria-label={t('items.documents.name')}
                              value={draftName}
                              onChange={(e) => setDraftName(e.currentTarget.value)}
                              maxLength={255}
                            />
                            <TextInput
                              size="xs"
                              aria-label={t('items.documents.description')}
                              placeholder={t('items.documents.descriptionPlaceholder')}
                              value={draftDescription}
                              onChange={(e) => setDraftDescription(e.currentTarget.value)}
                            />
                          </Stack>
                        ) : (
                          <>
                            {/* An anchor rather than a button: it downloads, which is what an
                                anchor means. It has no href because the bytes need a header an
                                <a> cannot send — see downloadItemDocument. */}
                            <Anchor
                              component="button"
                              type="button"
                              size="sm"
                              onClick={() => download.mutate(d)}
                            >
                              {d.documentName}
                            </Anchor>
                            {d.description && (
                              <Text size="xs" c="dimmed">
                                {d.description}
                              </Text>
                            )}
                          </>
                        )}
                      </Table.Td>

                      <Table.Td ta="right">
                        <Text size="xs" c="dimmed" ff="monospace">
                          {readableSize(d.sizeBytes)}
                        </Text>
                      </Table.Td>

                      <Table.Td>
                        <Group gap={2} justify="flex-end" wrap="nowrap">
                          {isEditing ? (
                            <>
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="green"
                                aria-label={t('items.documents.save')}
                                loading={rename.isPending}
                                disabled={!draftName.trim()}
                                onClick={() =>
                                  rename.mutate({
                                    id: d.itemDocumentId,
                                    name: draftName.trim(),
                                    description: draftDescription,
                                  })
                                }
                              >
                                <IconCheck size={14} />
                              </ActionIcon>
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                aria-label={t('items.documents.cancel')}
                                onClick={() => setEditing(null)}
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            </>
                          ) : (
                            <>
                              <Tooltip label={t('items.documents.download')} withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  aria-label={t('items.documents.downloadFor', { name: d.documentName })}
                                  loading={download.isPending}
                                  onClick={() => download.mutate(d)}
                                >
                                  <IconDownload size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t('items.documents.rename')} withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  aria-label={t('items.documents.renameFor', { name: d.documentName })}
                                  disabled={!canWrite || busy}
                                  onClick={() => startEditing(d)}
                                >
                                  <IconPencil size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t('items.documents.remove')} withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="red"
                                  aria-label={t('items.documents.removeFor', { name: d.documentName })}
                                  disabled={!canWrite || busy}
                                  onClick={() => remove.mutate(d.itemDocumentId)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </Stack>
  );
}
